import { useState } from "react";
import { motion } from "framer-motion";

import useFinanceAssinaturas from "@/hooks/useFinanceAssinaturas";
import HeroAssinaturasCard from "./assinaturas/HeroAssinaturasCard";
import AssinaturasToolbar from "./assinaturas/AssinaturasToolbar";
import AssinaturasGrid from "./assinaturas/AssinaturasGrid";
import AssinaturaModal from "./assinaturas/AssinaturaModal";
import SugestoesModal from "./assinaturas/SugestoesModal";

// FIN-6 — AssinaturasTab orquestrador.
// Hierarquia: Hero amber → Toolbar (T16/T17) → Lista cards (T16) →
// Modal Add/Edit (T17) → Modal Sugestões (T18).

const ROW_TRANSITION = { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] };

export default function AssinaturasTab({ onTalk }) {
  const sub = useFinanceAssinaturas();
  const [modalState, setModalState] = useState({ open: false, subscription: null });
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const openCreate = () => setModalState({ open: true, subscription: null });
  const openEdit = (item) => setModalState({ open: true, subscription: item });
  const closeModal = () => setModalState({ open: false, subscription: null });

  const handleDetect = async () => {
    setSuggestionsOpen(true);
    await sub.detectSuggestions();
  };

  const buildPrompt = (s) => {
    const amt = Number(s.amount || 0).toLocaleString("pt-BR", {
      style: "currency", currency: "BRL",
    });
    const freqLabel = { monthly: "ao mês", annual: "ao ano", weekly: "por semana", custom: "" }[s.frequency] || "";
    return `Conta tudo sobre minha assinatura ${s.name}. Estou pagando ${amt} ${freqLabel}. Vale a pena manter?`;
  };

  return (
    <div className="space-y-5 md:space-y-6" data-testid="finance-assinaturas">
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={ROW_TRANSITION}
      >
        <HeroAssinaturasCard
          summary={sub.summary}
          loading={sub.isLoading}
        />
      </motion.section>

      {/* Toolbar com filtros + ações */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.08 }}
      >
        <AssinaturasToolbar
          statusFilter={sub.statusFilter}
          onChangeStatus={sub.setStatusFilter}
          statusOptions={sub.statusOptions}
          exportCsv={sub.exportCsv}
          onCreate={openCreate}
          onDetect={handleDetect}
          isLoadingSuggestions={sub.isLoadingSuggestions}
        />
      </motion.section>

      {/* Lista de cards */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.14 }}
      >
        <AssinaturasGrid
          subscriptions={sub.filteredSubscriptions}
          isLoading={sub.isLoading}
          hasActiveFilter={sub.statusFilter !== "Todas"}
          onCreate={openCreate}
          onDetect={handleDetect}
          onEdit={openEdit}
          onDelete={async (item) => {
            if (window.confirm(`Apagar "${item.name}"? Essa ação não pode ser desfeita.`)) {
              try { await sub.deleteSubscription(item.id); } catch (_) { /* hook trata */ }
            }
          }}
          onTalk={onTalk ? (item) => onTalk(buildPrompt(item)) : null}
        />
      </motion.section>

      {sub.error && (
        <div
          className="text-caption text-status-error px-2"
          data-testid="sub-error"
        >
          {sub.error}
        </div>
      )}

      <AssinaturaModal
        open={modalState.open}
        onOpenChange={(o) => { if (!o) closeModal(); }}
        subscription={modalState.subscription}
        onCreate={sub.createSubscription}
        onUpdate={sub.updateSubscription}
        knownCategories={sub.knownCategories}
      />

      <SugestoesModal
        open={suggestionsOpen}
        onOpenChange={setSuggestionsOpen}
        suggestions={sub.suggestions}
        isLoading={sub.isLoadingSuggestions}
        onAdd={sub.addFromSuggestion}
      />
    </div>
  );
}
